// AssetCategories.jsx — versão com “subdiscipline” opcional e edição total para admin
import { useEffect, useState } from 'react';
import { Button, notification } from 'antd';
import { PlusOutlined, CloseCircleOutlined, AppstoreAddOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import moment from 'moment';

import TripleColumnTemplate from '../../../../Template/layoutComponents/TripleColumn';
import DoubleColumnTemplate from '../../../../Template/layoutComponents/DoubleColumn';
import Input from '../../../../Template/layoutComponents/Input';
import Select from '../../../../Template/layoutComponents/SingleSelect';
import Forms from '../../../../Template/layoutComponents/Forms';
import Tree from '../../../components/Tree';
import Checkbox from '../../../../Template/layoutComponents/Checkmark';
import Tooltip from '../../../../Template/layoutComponents/Tooltip';
import CustomOrderByButton from '../../../../Template/layoutComponents/OrderButton';
import SecondaryCategories from '../../../components/SecondaryCategories';
import InstrumentCategoryForm from '../../../components/InstrumentCategoryForm';
import useSettingsState from '../../../atoms/variables';
import useGlobalState from '../../../../Template/atoms/variables';
import { useTranslation } from 'react-i18next';

import {
  getAllCategories,
  getInstrumentCategories,
  getInterconnectionCategories,
  newCategory,
  newInstumentCategory,
  newInterconnectionCategory,
  updateCategory,
  updateInstrumentCategory,
  updateInterconnectionCategory,
} from '../../../helpers/apiCalls';

import { 
  defaultInstrumentCategory,
  instrumentCategorySchema 
} from '../../../schemas/instrumentCategorySchema';

import search from '../../../images/search.svg';
import viewDetails from '../../../../Maintenance/images/viewDetails.svg';

import './style.css';

const AssetCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const { t } = useTranslation();
  const { chosenType } = useParams();

  const { chosenPositions, setViewDetailsCategory, setViewDetailsCategories } = useSettingsState();

  const [notificationApi, contextHolder] = notification.useNotification();
  const { setPendingApiCalls, companyInfo, user } = useGlobalState();
  const isAdmin = user?.access?.includes('admin');

  const [categoriesForKind, setCategoriesForKind] = useState([]);
  const [chosenPosition, setChosenPosition] = useState(location.state?.selectedNode || null);
  const [chosenCategory, setChosenCategory] = useState(null);

  const [searchString, setSearchString] = useState(null);
  const [asc, setAsc] = useState(true);

  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(false);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationContent, setNotificationContent] = useState('');

  const [certificationInterval, setCertificationInterval] = useState(365);
  const [certificationScale, setCertificationScale] = useState(1);

  /* --------------------------------------------------------------------- */
  /*  Helpers                                                              */
  /* --------------------------------------------------------------------- */
  const labelMapping = {
    plant: 'plant',
    area: 'area',
    'sub-area': 'sub_area',
    line: 'line',
    machine: 'machine',
    assembly: 'assembly',
    equipment: 'equipment',
    component: 'component',
  };

  const subdisciplineOptionsMapping = {
    instrumentation: [
      { id: 'digital', label: t('digital') },
      { id: 'analog', label: t('analog') },
    ],
    electrical: [
      { id: 'panel', label: t('panel') },
      { id: 'field', label: t('field') },
    ],
    mechanical: [
      { id: 'rotative', label: t('rotating') },
      { id: 'static', label: t('static') },
    ],
    plumbing: [{ id: 'line', label: t('lines') }],
  };

  const translateNodes = (nodes) =>
    nodes?.map((node) => ({
      ...node,
      label: t(labelMapping[node.type] || node.label.toLowerCase()),
    }));

  /* --------------------------------------------------------------------- */
  /*  API calls                                                            */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    if (!['assets', 'positions', 'instruments', 'interconnections'].includes(chosenType)) {
      navigate('/settings/asset-tree/manage');
    } else {
      triggerApiCalls();
    }
  }, []);

  const triggerApiCalls = async () => {
    try {
      setPendingApiCalls((curr) => curr.concat(['getAllCategories']));
      let response;
      if (chosenType === 'instruments') {
        response = await getInstrumentCategories(instance);
      } else if (chosenType === 'interconnections') {
        response = await getInterconnectionCategories(instance);
      } else {
        response = await getAllCategories(instance);
      }
      setCategoriesForKind(response);
    } catch (err) {
      notificationApi.error({
        message: 'Falha ao carregar categorias existentes',
        description: err?.response?.data?.message
          ? `${err.response.data.message} (${err.response.status})`
          : err.message,
        duration: 5,
      });
    } finally {
      setPendingApiCalls((curr) => curr.filter((i) => i !== 'getAllCategories'));
    }
  };

  /* --------------------------------------------------------------------- */
  /*  Default category template                                             */
  /* --------------------------------------------------------------------- */
  const defaultCategory = chosenType === 'instruments' 
    ? {
        new: true,
        label: t('new_category'),
        instrumentData: defaultInstrumentCategory,
        certifiable: false,
        certification_cycle: 365,
      }
    : {
        new: true,
        label: t('new_category'),
        type: chosenPosition?.type,
        properties: [
          {
            label: t('name'),
            type: 'text',
            value: t('new_category'),
            main: true,
            required: true,
            disabled: false,
            shouldHoldPlace: true,
            key: 'name',
          },
          {
            label: t('acronym'),
            type: 'text',
            value: '',
            main: true,
            required: true,
            disabled: false,
            maxLength: 4,
            key: 'abbrev',
          },
          chosenType === 'assets' && {
            label: t('discipline'),
            type: 'singleSelect',
            value: '',
            options: [
              { id: 'mechanical', label: t('mechanical') },
              { id: 'electrical', label: t('electrical') },
              { id: 'plumbing', label: t('plumbing') },
              { id: 'instrumentation', label: t('instrumentation') },
            ],
            main: true,
            required: true,
            disabled: false,
            key: 'discipline',
          },
          chosenType === 'assets' && {
            label: t('subdiscipline'),
            type: 'singleSelect',
            value: '',
            options: [],
            main: true,
            required: false,
            disabled: !isAdmin,
            key: 'subdiscipline',
          },
          chosenType === 'positions' &&
            chosenPosition?.type === 'plant' && {
              label: t('icon'),
              type: 'singleSelect',
              value: '',
              options: [
                { id: 'datacenter', label: t('datacenter') },
                { id: 'vessel', label: t('vessel') },
                { id: 'factory', label: t('factory') },
                { id: 'system', label: t('system') },
              ],
              main: true,
              required: true,
              disabled: false,
              key: 'icon',
            },
        ].filter(Boolean),
        commissioning: { phases: [] },
        commissionable: false,
        certifiable: chosenType === 'instruments' ? false : undefined,
        certification_cycle: chosenType === 'instruments' ? 365 : undefined,
      };

  /* --------------------------------------------------------------------- */
  /*  Disable SAVE / CREATE button                                         */
  /* --------------------------------------------------------------------- */
  const disableCreateSave = () => {
    const isInvalid = (prop) => {
      if (prop.key === 'subdiscipline') return false;

      const empty =
        [null, ''].includes(prop.value) ||
        (typeof prop.value === 'string' && prop.value.trim() === '');

      if (prop.main && prop.required && empty) return true;

      if (
        prop.required &&
        prop.type.toLowerCase().includes('select') &&
        (!prop.options || prop.options.length === 0)
      )
        return true;

      return (
        prop.value === t('new_category') ||
        !prop.type ||
        prop.label.trim() === '' ||
        prop.label === t('new_property') ||
        (prop.options?.some((opt) => opt?.label === '') ?? false)
      );
    };

    if (creatingCategory) {
      const duplicate =
        categoriesForKind.some((cat) =>
          cat.properties
            .filter((prop) => prop.main && prop.key !== 'subdiscipline')
            .some((prop) =>
              chosenCategory?.properties
                .filter((p) => p.main && p.key !== 'subdiscipline')
                .some((p) => prop.key === p.key && (prop.value === p.value || p.value === '')),
            ),
        ) || chosenCategory.properties.some(isInvalid);

      return duplicate;
    }

    if (editingCategory) {
      return chosenCategory.properties.some(isInvalid);
    }

    return true;
  };

  /* --------------------------------------------------------------------- */
  /*  Effects                                                               */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    setChosenCategory(null);
    setCreatingCategory(false);
    setEditingCategory(false);
  }, [t]);

  useEffect(() => {
    setCertificationScale(1);

    const exists = categoriesForKind.some((cat) =>
      cat.properties
        .filter((prop) => prop.main)
        .some((prop) =>
          chosenCategory?.properties
            .filter((p) => p.main)
            .some(
              (p) => prop.key === p.key && prop.value !== '' && prop.value === p.value && !!p.value,
            ),
        ),
    );
    if (exists && chosenCategory) setNotificationContent(chosenCategory.properties[0].label);
    setShowNotification(exists);
  }, [chosenCategory]);

  /* --------------------------------------------------------------------- */
  /*  JSX helpers (columns)                                                 */
  /* --------------------------------------------------------------------- */

  /** LIST column (left) */
  const listColumn = (
    <div className='columnContainer' style={{ padding: 0 }}>
      <div className='columnHeader' style={{ padding: '2rem 2rem 0 2rem' }}>
        <div className='columnTitle'>{t('categories')}</div>
        <Button
          disabled={
            (chosenType !== 'instruments' &&
              chosenType !== 'interconnections' &&
              !chosenPosition) ||
            creatingCategory ||
            editingCategory
          }
          icon={<PlusOutlined />}
          size='large'
          style={{
            background:
              (chosenType !== 'instruments' &&
                chosenType !== 'interconnections' &&
                !chosenPosition) ||
              creatingCategory ||
              editingCategory
                ? 'grey'
                : 'black',
            color: 'white',
          }}
          onClick={() => {
            setEditingCategory(false);
            setCreatingCategory(true);
            setChosenCategory(defaultCategory);
          }}
        >
          {t('new_category')}
        </Button>
      </div>

      <div className='searchControls' style={{ padding: '1.5rem 2rem 0 2rem' }}>
        <Input
          value={searchString ?? ''}
          onChange={(e) => setSearchString(e.target.value ?? null)}
          style={{ maxWidth: '80%' }}
          placeholder={t('search')}
          icon={<img src={search} />}
        />
        <CustomOrderByButton onClick={() => setAsc((c) => !c)} />
      </div>

      <div className='categoryList'>
        {categoriesForKind
          .filter((cat) => 
            chosenType === 'instruments' || chosenType === 'interconnections' 
              ? true 
              : cat.type === chosenPosition?.type
          )
          .filter((cat) =>
            searchString ? 
              (chosenType === 'instruments' ? 
                (cat.name || cat.label).toLowerCase().includes(searchString.toLowerCase()) :
                cat.label.toLowerCase().includes(searchString.toLowerCase())
              ) : true,
          )
          .concat(creatingCategory ? [defaultCategory] : [])
          .sort((a, b) => {
            const aLabel = chosenType === 'instruments' ? (a.name || a.label) : a.label;
            const bLabel = chosenType === 'instruments' ? (b.name || b.label) : b.label;
            return a.new ? -1 : asc ? (aLabel < bLabel ? -1 : 1) : aLabel < bLabel ? 1 : -1;
          })
          .map((category, index) =>
            category.new ? (
              <div
                key='newcat'
                className={`categoryListItem ${chosenCategory?.id === category.id ? 'selected' : ''} creating`}
              >
                {category.label}
              </div>
            ) : (
              <>
                {index === 0 && <div key={`div-${category.id}`} className='categoryListDivider' />}
                <div
                  key={category.id}
                  className={`categoryListItem ${chosenCategory?.id === category.id ? 'selected' : ''} ${
                    creatingCategory || editingCategory ? 'disabled' : ''
                  }`}
                  onClick={() => {
                    if (creatingCategory || editingCategory) return;

                    const selected = JSON.parse(JSON.stringify(category));
                    
                    if (chosenType === 'instruments') {
                      // Para instrumentos, usar o formato de dados específico
                      selected.instrumentData = selected.instrumentData || defaultInstrumentCategory;
                      setChosenCategory(selected);
                      setSearchString(null);
                      return;
                    }

                    const isAsset = selected.properties?.some((p) => p.key === 'discipline');

                    if (isAsset) {
                      const hasSub = selected.properties.some((p) => p.key === 'subdiscipline');
                      const disciplineProp = selected.properties.find(
                        (p) => p.key === 'discipline',
                      );
                      const disciplineValue = disciplineProp?.value?.id || disciplineProp?.value;
                      const newOptions = subdisciplineOptionsMapping[disciplineValue] || [];

                      if (!hasSub) {
                        const idx = selected.properties.findIndex((p) => p.key === 'discipline');
                        selected.properties.splice(idx + 1, 0, {
                          label: t('subdiscipline'),
                          type: 'singleSelect',
                          value: '',
                          main: true,
                          required: false,
                          disabled: !isAdmin,
                          key: 'subdiscipline',
                        });
                      }

                      selected.properties = selected.properties.map((prop) =>
                        prop.key === 'subdiscipline'
                          ? {
                              ...prop,
                              options: newOptions,
                              disabled: !isAdmin,
                            }
                          : prop,
                      );
                    }

                    if (isAdmin && selected.properties) {
                      selected.properties = selected.properties.map((p) => ({
                        ...p,
                        disabled: false,
                      }));
                    }

                    setChosenCategory(selected);
                    setSearchString(null);
                  }}
                >
                  {chosenType === 'instruments' ? (category.name || category.label) : category.label}
                </div>
                <div key={`div2-${category.id}`} className='categoryListDivider' />
              </>
            ),
          )}
      </div>
    </div>
  );

  /** EDIT column (right) */
  const editColumn = (
    <div
      className='columnContainer'
      style={{
        padding: 0,
        justifyContent: 'space-between',
        maxHeight: 'calc(100vh - 6rem)',
        overflow: 'auto',
      }}
    >
      {chosenCategory && (
        <div className='columnHeader' style={{ padding: '2rem 2rem 0 2rem' }}>
          <div className='columnTitle'>
            {chosenType === 'instruments' ? 
              (chosenCategory.name || chosenCategory.label || t('new_category')) :
              (chosenCategory.label?.length ? chosenCategory.label : t('new_category'))
            }
          </div>

          {chosenCategory && !chosenCategory.new && !creatingCategory && (
            <div
              className={`viewDetailsContainer ${editingCategory ? 'disabled' : ''}`}
              onClick={() => {
                if (editingCategory) return;
                setViewDetailsCategory(chosenCategory);
                setViewDetailsCategories(categoriesForKind);
                navigate(`/settings/asset-tree/manage/${chosenType}/${chosenCategory.id}`);
              }}
            >
              <div className='viewDetailsIcon'>
                <img src={viewDetails} />
              </div>
              <div className='viewDetailsText'>{t('view_details')}</div>
            </div>
          )}
        </div>
      )}

      <div className='categoryPropertiesContainer' style={{ padding: '1.5rem 2rem 5rem 2rem' }}>
        {chosenType === 'instruments' ? (
          <InstrumentCategoryForm
            initialData={chosenCategory?.instrumentData || defaultInstrumentCategory}
            onSubmit={async (data) => {
              try {
                setPendingApiCalls((current) => current.concat(['saveInstrumentCategory']));
                
                if (creatingCategory) {
                  const mongoItem = await newInstumentCategory(instance, data);
                  setCategoriesForKind((current) =>
                    current.concat([
                      {
                        ...data,
                        id: mongoItem._id ?? mongoItem,
                        instrumentData: data,
                      },
                    ]),
                  );
                  setCreatingCategory(false);
                  setChosenCategory(null);
                } else {
                  await updateInstrumentCategory(instance, { ...data, id: chosenCategory.id });
                  setCategoriesForKind((current) =>
                    current.map((cat) =>
                      cat.id === chosenCategory.id
                        ? { ...cat, ...data, instrumentData: data }
                        : cat
                    )
                  );
                  setEditingCategory(false);
                }
                
                notificationApi.success({
                  message: creatingCategory ? 'Categoria criada com sucesso' : 'Categoria atualizada com sucesso',
                  duration: 3,
                });
              } catch (err) {
                notificationApi.error({
                  message: creatingCategory ? 'Falha ao criar categoria' : 'Falha ao atualizar categoria',
                  description: err?.response?.data?.message
                    ? `${err?.response?.data?.message} (${err?.response?.status})`
                    : err?.message,
                  duration: 5,
                });
              } finally {
                setPendingApiCalls((current) => current.filter((item) => item !== 'saveInstrumentCategory'));
              }
            }}
            onCancel={() => {
              setEditingCategory(false);
              setCreatingCategory(false);
              setChosenCategory(null);
            }}
            isLoading={false}
          />
        ) : (
          <>
            <Forms
              inputs={chosenCategory?.properties.filter((p) => p.main)}
              onInputBlur={() => {
                if (showNotification) {
                  notificationApi.info({
                    message: `Valores repetidos`,
                    description: `Valor do campo '${notificationContent}' já cadastrado. Use outro valor.`,
                    duration: 5,
                  });
                }
              }}
              onInputChange={(event, inputIndex) => {
                if (!creatingCategory) setEditingCategory(true);

                setChosenCategory((curr) => {
                  if (!curr) return curr;

                  let updated = curr.properties.map((item, idx) => {
                    if (idx === inputIndex) {
                      const newValue = item.type.toLowerCase().includes('select')
                        ? { id: event.target.value, label: '' }
                        : event.target.value;
                      return { ...item, value: newValue };
                    }
                    return item;
                  });

                  const changed = updated[inputIndex];
                  if (changed.key === 'discipline') {
                    const newDisciplineId = changed.value.id;
                    const newOptions = subdisciplineOptionsMapping[newDisciplineId] || [];

                    updated = updated.map((prop) =>
                      prop.key === 'subdiscipline'
                        ? {
                            ...prop,
                            options: newOptions,
                            value: '',
                            disabled: creatingCategory ? newOptions.length === 0 : !isAdmin,
                          }
                        : prop,
                    );
                  }

                  return { ...curr, properties: updated, label: updated[0].value };
                });
              }}
            />

            <SecondaryCategories
              chosenCategory={chosenCategory}
              setChosenCategory={setChosenCategory}
              setEditingCategory={setEditingCategory}
              companyInfo={companyInfo}
            />
          </>
        )}
      </div>

      {chosenType !== 'instruments' && (
        <div className='manageCategoriesButtonsContainer'>
          {(creatingCategory || editingCategory) && (
            <Button
              id='cancel'
              size='large'
              icon={<CloseCircleOutlined />}
              style={{ borderColor: 'black', background: 'white', color: 'black' }}
              onClick={() => {
                setEditingCategory(false);
                setCreatingCategory(false);
                setChosenCategory(null);
              }}
            >
              {t('cancel_changes')}
            </Button>
          )}

          {chosenCategory && (
            <Button
              id='create'
              className={disableCreateSave() ? 'disabled' : ''}
              size='large'
              disabled={disableCreateSave()}
              style={{ borderColor: '#1777FF', background: '#1777FF', color: 'white' }}
              icon={<AppstoreAddOutlined />}
              onClick={async () => {
                if (creatingCategory) {
                  try {
                    setPendingApiCalls((current) => current.concat(['newCategory']));
                    let mongoItem;
                    if (chosenType === 'interconnections') {
                      mongoItem = await newInterconnectionCategory(instance, chosenCategory);
                    } else {
                      mongoItem = await newCategory(instance, chosenCategory);
                    }

                    setCategoriesForKind((current) =>
                      current.concat([
                        {
                          ...chosenCategory,
                          properties: chosenCategory.properties.map((prop) => {
                            if (prop.main) prop.disabled = true;
                            return prop;
                          }),
                          new: false,
                          id: mongoItem._id ?? mongoItem,
                        },
                      ]),
                    );
                  } catch (err) {
                    notificationApi.error({
                      message: 'Falha ao criar nova categoria',
                      description: err?.response?.data?.message
                        ? `${err?.response?.data?.message} (${err?.response?.status})`
                        : err?.message,
                      duration: 5,
                    });
                  } finally {
                    setChosenCategory(null);
                    setPendingApiCalls((current) => current.filter((item) => item !== 'newCategory'));
                  }
                } else {
                  setCategoriesForKind((current) =>
                    current
                      .filter((cat) => cat.id != chosenCategory.id)
                      .concat([{ ...chosenCategory }]),
                  );

                  try {
                    setPendingApiCalls((current) => current.concat(['updateCategory']));

                    if (chosenType === 'interconnections') {
                      await updateInterconnectionCategory(instance, chosenCategory);
                    } else {
                      await updateCategory(instance, chosenCategory);
                    }
                  } catch (err) {
                    notificationApi.error({
                      message: 'Falha ao atualizar categoria existente',
                      description: err?.response?.data?.message
                        ? `${err?.response?.data?.message} (${err?.response?.status})`
                        : err?.message,
                      duration: 5,
                    });
                  } finally {
                    setPendingApiCalls((current) =>
                      current.filter((item) => item !== 'updateCategory'),
                    );
                  }
                }
                setCreatingCategory(false);
                setEditingCategory(false);
              }}
            >
              {creatingCategory ? t('create_item_settings') : t('save_item_settings')}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  /* --------------------------------------------------------------------- */
  /*  Return                                                               */
  /* --------------------------------------------------------------------- */
  return (
    <div id='assetCategories'>
      {contextHolder}

      {chosenType === 'instruments' || chosenType === 'interconnections' ? (
        <DoubleColumnTemplate
          columnColors={['linear-gradient(180deg, #FDFDFD 0%, #F7F7F7 100%)', '#FFFFFF']}
          firstColumnContent={listColumn}
          secondColumnContent={editColumn}
        />
      ) : (
        <TripleColumnTemplate
          firstColumnContent={
            <div className='columnContainer'>
              <div className='columnHeader' style={{ minHeight: '3rem', flexBasis: 'none' }}>
                <div className='columnTitle'>
                  {chosenType === 'positions' ? t('my_positions') : t('my_assets')}
                </div>
              </div>
              <Tree
                disabled={creatingCategory || editingCategory}
                treeNodes={translateNodes(
                  chosenPositions?.filter((node) => chosenType.includes(node.kind)),
                )}
                maxNodeWidth='90%'
                nodeType={chosenType}
                chosenPositions={[chosenPosition]}
                setChosenPosition={setChosenPosition}
                setChosenCategory={setChosenCategory}
                setSearchString={setSearchString}
              />
            </div>
          }
          secondColumnContent={listColumn}
          thirdColumnContent={editColumn}
        />
      )}
    </div>
  );
};

export default AssetCategories;
